import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useChosenDepartment } from "../../contexts/ChosenDepartment/hooks";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
	type CareersNotificationData,
	careersNotificationDestination,
	careersNotificationKind,
	parseCareersNotificationData,
} from "../../pages/Careers/careersNotification";
import { localStorageService } from "../../services/LocalStorageService";
import { trackErrorEvent } from "../../utils/analytics";
import { CAREERS_PATH } from "../../utils/careersRoutes";
import type { NotificationResponse } from "../../utils/models";

type Parsed = { data: CareersNotificationData; error: null } | { data: null; error: unknown };

/**
 * The bell's side of a Careers notification — an @mention in a note or a feedback, or a request for an
 * interviewer's feedback: its data, and opening it. Null for any other notification type, so the item can branch on it.
 *
 * Opening lands on the candidate's profile (on the mentioning entry, or the Feedback tab). Careers' URL carries no
 * department (the page reads the chosen one), so when the candidate is in another department this first switches to
 * it — the chosen department and the stored choice, as a `/{department}/…` link does for other notifications. A
 * department the reader is no longer in can't be switched to: they are told so and nothing opens.
 *
 * Data that can't be read is reported, and the item then shows the row's own English title and message, and
 * opens Careers.
 */
export const useCareersNotification = (notification: NotificationResponse) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { userInfo } = useAuth();
	const { chosenDepartment, setChosenDepartment } = useChosenDepartment();
	const { showSnackbar } = useSnackbar();
	const kind = careersNotificationKind(notification.notification_type);

	const parsed = useMemo((): Parsed | null => {
		if (kind === undefined) return null;
		try {
			return { data: parseCareersNotificationData(notification.notification_data, kind), error: null };
		} catch (error) {
			return { data: null, error };
		}
	}, [kind, notification.notification_data]);

	useEffect(() => {
		if (parsed === null || parsed.error === null) return;
		trackErrorEvent("careers_notification_parse_failed", parsed.error, { notification_id: notification.id, notification_type: notification.notification_type });
	}, [parsed, notification.id, notification.notification_type]);

	if (parsed === null) return null;

	/** Switch department if needed and go; returns where it went, or null when the department can't be reached. */
	const open = (): string | null => {
		const departmentId = notification.department_id;
		if (chosenDepartment.department_id !== departmentId) {
			const department = userInfo.departments?.find((candidate) => candidate.department_id === departmentId);
			if (department === undefined) {
				trackErrorEvent("careers_notification_department_unavailable", new Error("Not a member of the candidate's department"), {
					notification_id: notification.id,
					department_id: departmentId,
				});
				showSnackbar(t("careers.notification.no_department"), "error");
				return null;
			}
			localStorageService.setChosenDepartmentID(departmentId);
			setChosenDepartment(department);
		}
		const destination = parsed.data === null ? CAREERS_PATH : careersNotificationDestination(parsed.data);
		navigate(destination);
		return destination;
	};

	return { data: parsed.data, open };
};
